/**
 * Global scroll controller.
 *
 * Owns the single Lenis instance for the whole theme and exposes it as
 * `window.themeScroll`. Components must go through this rather than
 * constructing their own Lenis or attaching their own window scroll listener.
 *
 * LOAD ORDER — READ THIS BEFORE USING IT FROM A COMPONENT.
 *
 * This file must load AFTER lenis.min.js; both are `defer` in
 * layout/theme.liquid and defer preserves document order, so that part holds.
 *
 * It does NOT hold for components. A section's `{% javascript %}` bundle can and
 * does run BEFORE this file — measured in Chrome, not assumed, after a header
 * that subscribed unconditionally silently never received a scroll event. So:
 *
 *   NEVER assume window.themeScroll exists when your component upgrades.
 *
 * Use this shape instead. It is correct whichever order the two land in:
 *
 *   connectedCallback() {
 *     if (window.themeScroll) this.bindScroll();
 *     else document.addEventListener('themescroll:ready', this.bindScroll, { once: true });
 *   }
 *   disconnectedCallback() {
 *     document.removeEventListener('themescroll:ready', this.bindScroll);
 *     if (this.bound) window.themeScroll.off('scroll', this.onScroll);
 *   }
 *
 * `themescroll:ready` is dispatched on `document` at the bottom of this file,
 * once, with the controller as `event.detail`.
 *
 * REDUCED MOTION: when the user prefers reduced motion, Lenis is never
 * started and `themeScroll.lenis` is null — the page uses native scrolling.
 * Every method below still works, so callers do NOT need to null-check.
 * Components that animate on scroll should check `themeScroll.prefersReducedMotion`
 * and render their final/resting state immediately rather than skipping the
 * animation and leaving content invisible or mid-transition.
 *
 * @example  Reveal on scroll, with a reduced-motion fallback
 *   connectedCallback() {
 *     if (window.themeScroll.prefersReducedMotion) {
 *       this.classList.add('is-visible');   // final state, no animation
 *       return;
 *     }
 *     this.onScroll = () => { ... };
 *     window.themeScroll.on('scroll', this.onScroll);
 *   }
 *   disconnectedCallback() {
 *     window.themeScroll.off('scroll', this.onScroll);
 *   }
 *
 * @example  Drawer that pauses scrolling while open
 *   open()  { window.themeScroll.lock('cart-drawer'); }
 *   close() { window.themeScroll.unlock('cart-drawer'); }
 */
(function () {
  'use strict';

  var REDUCED_MOTION = '(prefers-reduced-motion: reduce)';

  /** Tailwind utility toggled on <html> to hold scroll position while locked. */
  var LOCK_CLASS = 'overflow-hidden';

  var LENIS_OPTIONS = {
    autoRaf: true,
    allowNestedScroll: true,
  };

  var motionQuery = window.matchMedia(REDUCED_MOTION);

  /**
   * Keys of everything currently requesting a scroll lock.
   *
   * Counted rather than boolean so nested overlays behave: if a modal opens
   * on top of a drawer, the modal closing must not restore scrolling while
   * the drawer is still open.
   */
  var locks = new Set();

  /** Subscribers registered via `on('scroll', fn)`. */
  var scrollHandlers = new Set();

  var lenis = null;
  var nativeScrollBound = false;

  function currentScrollY() {
    return lenis ? lenis.scroll : window.scrollY;
  }

  function emitScroll(payload) {
    scrollHandlers.forEach(function (handler) {
      try {
        handler(payload);
      } catch (error) {
        // One bad subscriber must not break scrolling for everything else.
        console.error('[themeScroll] scroll handler failed', error);
      }
    });
  }

  function handleLenisScroll(instance) {
    emitScroll({ scroll: instance.scroll, velocity: instance.velocity });
  }

  function handleNativeScroll() {
    emitScroll({ scroll: window.scrollY, velocity: 0 });
  }

  /** Route scroll events from whichever source is currently active. */
  function bindScrollSource() {
    if (lenis) {
      if (nativeScrollBound) {
        window.removeEventListener('scroll', handleNativeScroll);
        nativeScrollBound = false;
      }
      lenis.on('scroll', handleLenisScroll);
    } else if (!nativeScrollBound) {
      window.addEventListener('scroll', handleNativeScroll, { passive: true });
      nativeScrollBound = true;
    }
  }

  function startLenis() {
    if (lenis || motionQuery.matches || typeof window.Lenis !== 'function') return;

    lenis = new window.Lenis(LENIS_OPTIONS);
    bindScrollSource();

    // A lock taken before Lenis existed must still apply to it.
    if (locks.size > 0) lenis.stop();
  }

  function stopLenis() {
    if (!lenis) return;

    lenis.off('scroll', handleLenisScroll);
    lenis.destroy();
    lenis = null;

    bindScrollSource();
  }

  /**
   * Respond to the OS setting changing mid-session, so a user turning
   * reduced motion on gets native scrolling without a reload.
   */
  function handleMotionPreferenceChange() {
    if (motionQuery.matches) {
      stopLenis();
    } else {
      startLenis();
    }
    themeScroll.lenis = lenis;
    themeScroll.prefersReducedMotion = motionQuery.matches;
  }

  var themeScroll = {
    /** The raw Lenis instance, or null under reduced motion. */
    lenis: null,

    prefersReducedMotion: motionQuery.matches,

    /** Current vertical scroll position, from whichever source is active. */
    get scrollY() {
      return currentScrollY();
    },

    get isLocked() {
      return locks.size > 0;
    },

    /**
     * Prevent the page from scrolling. Idempotent per key.
     * @param {string} key - Unique owner id, e.g. 'cart-drawer'.
     */
    lock: function (key) {
      if (!key) throw new Error('[themeScroll] lock() requires a unique key');
      if (locks.has(key)) return;

      locks.add(key);
      if (locks.size > 1) return; // Already locked by someone else.

      if (lenis) lenis.stop();
      document.documentElement.classList.add(LOCK_CLASS);
    },

    /**
     * Release this key's lock. Scrolling resumes only once every key has
     * released, so nested overlays cannot unlock each other.
     * @param {string} key
     */
    unlock: function (key) {
      if (!locks.delete(key)) return;
      if (locks.size > 0) return; // Something else still holds a lock.

      if (lenis) lenis.start();
      document.documentElement.classList.remove(LOCK_CLASS);
    },

    /** Release every lock. For teardown/error recovery, not routine use. */
    unlockAll: function () {
      locks.clear();
      if (lenis) lenis.start();
      document.documentElement.classList.remove(LOCK_CLASS);
    },

    /**
     * Subscribe to scroll. Works with or without Lenis.
     * @param {'scroll'} event
     * @param {(payload: {scroll: number, velocity: number}) => void} handler
     */
    on: function (event, handler) {
      if (event !== 'scroll') {
        throw new Error('[themeScroll] unsupported event: ' + event);
      }
      scrollHandlers.add(handler);
    },

    /** Unsubscribe. Always call this from `disconnectedCallback`. */
    off: function (event, handler) {
      if (event !== 'scroll') return;
      scrollHandlers.delete(handler);
    },

    /**
     * Scroll to a target. Falls back to native smooth/instant scrolling when
     * Lenis is not running.
     * @param {string|number|HTMLElement} target
     * @param {object} [options] - Passed through to Lenis when available.
     */
    scrollTo: function (target, options) {
      if (lenis) {
        lenis.scrollTo(target, options);
        return;
      }

      var element =
        typeof target === 'string' ? document.querySelector(target) : target;

      if (typeof target === 'number') {
        window.scrollTo({ top: target, behavior: 'auto' });
      } else if (element instanceof HTMLElement) {
        // 'auto' rather than 'smooth': this path is reduced-motion territory.
        element.scrollIntoView({ behavior: 'auto', block: 'start' });
      }
    },
  };

  startLenis();
  bindScrollSource();

  themeScroll.lenis = lenis;

  if (typeof motionQuery.addEventListener === 'function') {
    motionQuery.addEventListener('change', handleMotionPreferenceChange);
  }

  window.themeScroll = themeScroll;

  /**
   * Announce readiness for any component that upgraded before this file ran.
   * Dispatched after the global is assigned, so a listener can use it straight
   * away. See the LOAD ORDER note at the top for the consumer pattern.
   */
  document.dispatchEvent(
    new CustomEvent('themescroll:ready', { detail: themeScroll })
  );
})();
